import React, { useCallback, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
// Mightier Survey Engine
import {
  ChoicesResponse,
  // TODO: need to rename this in the lib
  ChoiceResponse as ChoiceResponseSelection,
  MultiSelectQuestion,
  SingleSelectQuestion,
} from "@neuromotion-inc/mightier-survey-engine";
import { SurveyEngineIface } from "src/services/surveyHooks/useSurveyEngine";
// Styling
import { Container, Text, Stack, Select } from "@mantine/core";

import { OptionButton } from "../options-button/options-button";

type SelectQuestionOption = MultiSelectQuestion["options"][number];

type AnySelectQuestion = MultiSelectQuestion | SingleSelectQuestion;

interface SurveySelectInputProps {
  question: AnySelectQuestion;
  surveyEngine: SurveyEngineIface;
  questionIdToValidationErrorsMap: Map<string, string[]>;
  testId: string;
}

/**
 * NOTE: I was dumb and accidentally made very confusing typing. This needs to be addressed in the survey engine,
 * which will likely require breaking change (type names, etc.)
 *
 * Temporarily I've encapsulated the gross logic for transposing the types within this class.
 *
 * Just remember:
 * - ChoicesResponse -> what you pass to surveyEngine.recordResponse()
 * - SelectQuestionOption -> what you get from the question.options array
 * - ChoiceResponseSelection:
 *    - very similar to `SelectQuestionOption`, but with a `label` field
 *    - ChoicesResponse['responseValue'] is an array of this object
 *    - named internally in SurveyEngine as `ChoiceResponse` (TODO: rename this in survey engine)
 *
 */

class SelectInputChoicesCalculator {
  // ugh eslint is bugged for this syntax, which handles assignment of the private fields inline
  // eslint-disable-next-line no-useless-constructor
  constructor(
    private question: AnySelectQuestion,
    private surveyEngine: SurveyEngineIface,
    private newSelection: SelectQuestionOption,
  ) {}

  getResponseObjToRecord(): ChoicesResponse {
    return {
      question_id: this.question.id,
      responseValue: this.getNewSelections(),
      type: "CHOICES",
    };
  }

  private getNewSelections(): ChoiceResponseSelection[] {
    if (this.question.type === "SINGLE_SELECT") {
      return this.setSelectionForSingleSelect();
    } else {
      return this.getNewSelectionsForMultiSelect();
    }
  }

  private choiceSelectionToChoiceResponse(
    selectedChoice: SelectQuestionOption,
  ): ChoiceResponseSelection {
    return {
      label: this.surveyEngine.getTextForChoiceLabel(
        this.question.id,
        selectedChoice.value,
      ),
      value: selectedChoice.value,
      extra: selectedChoice.additional_info,
    };
  }

  private getNewSelectionsForMultiSelect(): ChoiceResponseSelection[] {
    const prevResp = this.surveyEngine.getResponseForQuestion(this.question.id);
    // first time answering!!
    if (!prevResp) {
      return [this.choiceSelectionToChoiceResponse(this.newSelection)];
    }

    if (prevResp.type !== "CHOICES") {
      throw new Error("Previous response is not CHOICES");
    }

    const isAlreadyThere = prevResp.responseValue.some(
      (choice) => choice.value === this.newSelection.value,
    );
    if (isAlreadyThere) {
      return prevResp.responseValue.filter(
        (choice) => choice.value !== this.newSelection.value,
      );
    } else {
      return [
        ...prevResp.responseValue,
        this.choiceSelectionToChoiceResponse(this.newSelection),
      ];
    }
  }

  private setSelectionForSingleSelect(): ChoiceResponseSelection[] {
    const prevResp = this.surveyEngine.getResponseForQuestion(this.question.id);

    // If there's an existing response and it's the same as the new selection, deselect it
    if (
      prevResp &&
      prevResp.type === "CHOICES" &&
      prevResp.responseValue.length > 0
    ) {
      const currentSelection = prevResp.responseValue[0];
      if (currentSelection?.value === this.newSelection.value) {
        return []; // Deselect if already selected
      }
    }

    return [this.choiceSelectionToChoiceResponse(this.newSelection)];
  }
}

//
//
//

export const SurveySelectInput = React.memo<SurveySelectInputProps>(
  function SurveySelectInput({
    question,
    surveyEngine,
    questionIdToValidationErrorsMap,
    testId,
  }) {
    const { t } = useTranslation("translation");

    const selectedValues = useMemo(() => {
      const res = surveyEngine.getResponseForQuestion(
        question.id,
      ) as ChoicesResponse;
      return res?.responseValue ?? [];
    }, [surveyEngine, question.id]);

    const [touched, setTouched] = useState(true);

    const { errors, showError } = useMemo(() => {
      const errors = questionIdToValidationErrorsMap.get(question.id) ?? [];
      const showError = touched && errors.length > 0;
      return { errors, showError };
    }, [touched, question.id, questionIdToValidationErrorsMap]);

    const handleSelection = useCallback(
      (selectedOption: SelectQuestionOption) => {
        setTouched(true);

        const calculator = new SelectInputChoicesCalculator(
          question,
          surveyEngine,
          selectedOption,
        );
        const respToRecord = calculator.getResponseObjToRecord();
        surveyEngine.recordResponse(respToRecord);
      },
      [question, surveyEngine],
    );

    const onBlur = useCallback(() => {}, []);

    if (!question.options || question.options.length === 0) {
      return null;
    }

    /**
     * SINGLE SELECT DROPDOWN
     */

    if (
      question.type === "SINGLE_SELECT" &&
      question.ui.inputType === "DROPDOWN"
    ) {
      const selectOptions = question.options.map((option) => ({
        value: option.value,
        label: surveyEngine.getTextForChoiceLabel(question.id, option.value),
      }));

      const selectedValue = selectedValues[0];

      return (
        <Container>
          <Select
            data-testid={testId}
            placeholder={t("survey.selectAnswer")}
            data={selectOptions}
            value={selectedValue?.value}
            onChange={(value) => {
              const newlySelectedOpt = question.options.find(
                (option) => option.value === value,
              );
              if (!newlySelectedOpt) {
                return;
              }

              handleSelection(newlySelectedOpt);
            }}
            onBlur={onBlur}
            error={showError ? errors.join(", ") : null} // Show error only if touched
          />
        </Container>
      );
    }

    /**
     * BUTTON STACK
     */

    return (
      <Container>
        <Stack data-testid={testId}>
          {question.options.map((option, idx) => (
            <OptionButton
              key={idx}
              value={option.value}
              surveyEngine={surveyEngine}
              labelTranslationKey={option.label_translation_key}
              onChange={() => {
                handleSelection(option);
              }}
              isValueSelected={selectedValues.some(
                (v) => v.value === option.value,
              )}
              onBlur={onBlur}
            />
          ))}
          {/* Mantine Stack component doesn't have built in error state, so made own */}
          {showError ? (
            <Text mt="xl" color="red">
              {errors.join(", ")}
            </Text>
          ) : null}
        </Stack>
      </Container>
    );
  },
);
